import { useState, useEffect, useCallback, useRef } from "react";
import { provinces, regionData } from "@/mocks/regionData";

interface WeatherDay {
  date: string;
  weatherCode: number;
  tempMax: number;
  tempMin: number;
  precipProb: number;
}

interface SelectedLocation {
  province: string;
  district: string;
}

interface GeoResult {
  latitude: number;
  longitude: number;
}

const STORAGE_KEY = "weather_location_v2";
const GEO_CACHE_KEY = "weather_geo_cache_v2";

function getWeatherInfo(code: number): { text: string; icon: string; colorClass: string } {
  if (code === 0) return { text: "맑음", icon: "ri-sun-line", colorClass: "text-amber-500" };
  if (code === 1) return { text: "대체로 맑음", icon: "ri-sun-line", colorClass: "text-amber-400" };
  if (code === 2) return { text: "구름조금", icon: "ri-cloudy-line", colorClass: "text-gray-400" };
  if (code === 3) return { text: "흐림", icon: "ri-cloudy-line", colorClass: "text-gray-500" };
  if (code >= 45 && code <= 48) return { text: "안개", icon: "ri-mist-line", colorClass: "text-gray-400" };
  if (code >= 51 && code <= 55) return { text: "이슬비", icon: "ri-drizzle-line", colorClass: "text-sky-400" };
  if (code >= 61 && code <= 65) return { text: "비", icon: "ri-showers-line", colorClass: "text-sky-500" };
  if (code >= 66 && code <= 67) return { text: "진눈깨비", icon: "ri-hail-line", colorClass: "text-indigo-400" };
  if (code >= 71 && code <= 77) return { text: "눈", icon: "ri-snowy-line", colorClass: "text-indigo-400" };
  if (code >= 80 && code <= 82) return { text: "소나기", icon: "ri-showers-line", colorClass: "text-sky-600" };
  if (code >= 85 && code <= 86) return { text: "눈보라", icon: "ri-snowy-line", colorClass: "text-indigo-500" };
  if (code >= 95) return { text: "천둥번개", icon: "ri-thunderstorms-line", colorClass: "text-amber-600" };
  return { text: "맑음", icon: "ri-sun-line", colorClass: "text-amber-500" };
}

function loadGeoCache(): Record<string, GeoResult> {
  try {
    const raw = localStorage.getItem(GEO_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveGeoCache(cache: Record<string, GeoResult>) {
  try {
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore
  }
}

async function geocodeDistrict(province: string, district: string): Promise<GeoResult | null> {
  const cacheKey = `${province}_${district}`;
  const cache = loadGeoCache();
  if (cache[cacheKey]) return cache[cacheKey];

  try {
    const query = encodeURIComponent(district);
    const res = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${query}&count=5&language=ko&countryCode=KR`
    );
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.results || json.results.length === 0) return null;

    const result: GeoResult = {
      latitude: json.results[0].latitude,
      longitude: json.results[0].longitude,
    };
    const updated = { ...cache, [cacheKey]: result };
    saveGeoCache(updated);
    return result;
  } catch {
    return null;
  }
}

const provinceCoords: Record<string, GeoResult> = {
  "서울": { latitude: 37.57, longitude: 126.98 },
  "부산": { latitude: 35.18, longitude: 129.08 },
  "대구": { latitude: 35.87, longitude: 128.60 },
  "인천": { latitude: 37.46, longitude: 126.71 },
  "광주": { latitude: 35.16, longitude: 126.85 },
  "대전": { latitude: 36.35, longitude: 127.38 },
  "울산": { latitude: 35.54, longitude: 129.31 },
  "세종": { latitude: 36.48, longitude: 127.29 },
  "경기": { latitude: 37.26, longitude: 127.03 },
  "강원": { latitude: 37.88, longitude: 127.73 },
  "충북": { latitude: 36.64, longitude: 127.49 },
  "충남": { latitude: 36.60, longitude: 126.66 },
  "전북": { latitude: 35.82, longitude: 127.15 },
  "전남": { latitude: 34.99, longitude: 126.48 },
  "경북": { latitude: 36.57, longitude: 128.73 },
  "경남": { latitude: 35.23, longitude: 128.68 },
  "제주": { latitude: 33.50, longitude: 126.53 },
};

function getDayLabel(dateStr: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(dateStr + "T00:00:00");
  const diffDays = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "오늘";
  if (diffDays === 1) return "내일";
  if (diffDays === 2) return "모레";
  return dateStr;
}

function loadLocation(): SelectedLocation {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.province && parsed.district) return parsed as SelectedLocation;
    }
  } catch {
    // ignore
  }
  return { province: "서울", district: "" };
}

export default function WeatherWidget() {
  const [location, setLocation] = useState<SelectedLocation>(loadLocation);
  const [forecast, setForecast] = useState<WeatherDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [step, setStep] = useState<"province" | "district">("province");
  const [tempProvince, setTempProvince] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchWeather = useCallback(async (loc: SelectedLocation) => {
    setLoading(true);
    setError("");
    try {
      let coords: GeoResult | null = null;

      if (loc.district) {
        coords = await geocodeDistrict(loc.province, loc.district);
      }
      if (!coords) {
        coords = provinceCoords[loc.province] || null;
      }
      if (!coords) throw new Error("좌표를 찾을 수 없습니다.");

      const res = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${coords.latitude}&longitude=${coords.longitude}&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=Asia%2FSeoul&forecast_days=3`
      );
      if (!res.ok) throw new Error("날씨 데이터를 불러오지 못했습니다.");

      const json = await res.json();
      const daily = json.daily;
      const days: WeatherDay[] = daily.time.map((date: string, i: number) => ({
        date,
        weatherCode: daily.weathercode[i],
        tempMax: Math.round(daily.temperature_2m_max[i]),
        tempMin: Math.round(daily.temperature_2m_min[i]),
        precipProb: daily.precipitation_probability_max[i] ?? 0,
      }));
      setForecast(days);
    } catch {
      setError("날씨 정보를 가져오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeather(location);
  }, [location, fetchWeather]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setStep("province");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenDropdown = () => {
    setStep("province");
    setTempProvince(location.province);
    setDropdownOpen(true);
  };

  const handleSelectProvince = (province: string) => {
    const districts = regionData[province] || [];
    setTempProvince(province);
    if (districts.length === 0) {
      const newLoc: SelectedLocation = { province, district: "" };
      setLocation(newLoc);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newLoc));
      setDropdownOpen(false);
      setStep("province");
    } else {
      setStep("district");
    }
  };

  const handleSelectDistrict = (district: string) => {
    const newLoc: SelectedLocation = { province: tempProvince, district };
    setLocation(newLoc);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newLoc));
    setDropdownOpen(false);
    setStep("province");
  };

  const displayLabel = location.district
    ? `${location.province} ${location.district}`
    : location.province;

  const districts = regionData[tempProvince] || [];

  return (
    <div className="bg-white rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">날씨 현황</h3>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={handleOpenDropdown}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 text-xs text-gray-700 font-medium cursor-pointer whitespace-nowrap transition-colors max-w-[140px]"
          >
            <i className="ri-map-pin-line text-gray-400 flex-shrink-0"></i>
            <span className="truncate">{displayLabel}</span>
            <i className={`ri-arrow-down-s-line text-gray-400 text-xs flex-shrink-0 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl z-50 overflow-hidden"
              style={{ width: step === "district" ? "280px" : "140px" }}
            >
              {step === "province" && (
                <div className="max-h-56 overflow-y-auto py-1">
                  {provinces.map((province) => (
                    <button
                      key={province}
                      onClick={() => handleSelectProvince(province)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors ${
                        location.province === province ? "text-amber-600 font-semibold bg-amber-50/60" : "text-gray-700"
                      }`}
                    >
                      <span>{province}</span>
                      {(regionData[province]?.length ?? 0) > 0 && (
                        <i className="ri-arrow-right-s-line text-gray-300"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {step === "district" && (
                <div>
                  <div className="flex items-center gap-1 px-3 py-2.5 border-b border-gray-100">
                    <button
                      onClick={() => setStep("province")}
                      className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-arrow-left-s-line text-gray-500 text-sm"></i>
                    </button>
                    <span className="text-xs font-semibold text-gray-900">{tempProvince}</span>
                    <span className="text-xs text-gray-400 ml-1">시/군/구 선택</span>
                  </div>
                  <div className="grid grid-cols-3 max-h-52 overflow-y-auto p-2 gap-1">
                    {districts.map((district) => (
                      <button
                        key={district}
                        onClick={() => handleSelectDistrict(district)}
                        className={`px-2 py-1.5 text-xs rounded-lg cursor-pointer text-left transition-colors whitespace-nowrap overflow-hidden text-ellipsis ${
                          location.province === tempProvince && location.district === district
                            ? "bg-amber-500 text-white font-semibold"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        {district}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {error && !loading && (
        <div className="text-center py-6">
          <div className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-2">
            <i className="ri-cloud-off-line text-gray-300 text-lg"></i>
          </div>
          <p className="text-xs text-gray-400 mb-2">{error}</p>
          <button
            onClick={() => fetchWeather(location)}
            className="text-xs text-amber-600 hover:text-amber-700 font-medium cursor-pointer"
          >
            다시 시도
          </button>
        </div>
      )}

      {!loading && !error && forecast.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {forecast.map((day, i) => {
            const info = getWeatherInfo(day.weatherCode);
            return (
              <div
                key={day.date}
                className={`text-center p-2.5 rounded-lg ${i === 0 ? "bg-amber-50/70 ring-1 ring-amber-100" : "bg-gray-50"}`}
              >
                <p className={`text-xs font-medium ${i === 0 ? "text-amber-700" : "text-gray-400"}`}>
                  {getDayLabel(day.date)}
                </p>
                <div className="w-8 h-8 flex items-center justify-center mx-auto my-1.5">
                  <i className={`${info.icon} text-xl ${info.colorClass}`}></i>
                </div>
                <p className="text-xs text-gray-600 mb-1 leading-tight">{info.text}</p>
                <div className="flex items-center justify-center gap-1">
                  <span className="text-xs font-semibold text-gray-800">{day.tempMax}°</span>
                  <span className="text-xs text-gray-300">/</span>
                  <span className="text-xs text-gray-400">{day.tempMin}°</span>
                </div>
                {day.precipProb > 0 && (
                  <div className="flex items-center justify-center gap-0.5 mt-1">
                    <i className="ri-drop-line text-xs text-sky-400"></i>
                    <span className="text-xs text-sky-500">{day.precipProb}%</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}